"use client";

import { AnimatePresence, motion } from "motion/react";

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDanger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Shared confirm/cancel dialog for any "are you sure?" moment in the seller
 * dashboard — deleting a listing, confirming a booking, etc. Swap out
 * title/message/confirmText per call site; the dialog itself stays generic.
 *
 * Animation notes (mirrors the Motion docs patterns provided):
 *  - AnimatePresence + a mounted/unmounted child gives the exit animation
 *    when isOpen flips back to false (same shape as the ExitAnimation demo).
 *  - The backdrop fades, the card scales+fades in/out with a spring.
 *  - whileTap on both buttons gives the small "pressed" feedback from the
 *    whileTap gesture demo.
 */
export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  isDanger = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key="confirm-dialog-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onCancel}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            backgroundColor: "rgba(0,0,0,0.65)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <motion.div
            key="confirm-dialog-card"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ type: "spring", stiffness: 340, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0b0f0d] border border-[var(--t-900-40,#1c2a25)] rounded-2xl p-6 w-full max-w-sm shadow-2xl"
          >
            <h2 className="text-xl font-bold text-white mb-3">{title}</h2>
            <p className="text-sm text-stone-400 leading-relaxed mb-6">
              {message}
            </p>
            <div className="flex items-center justify-end gap-2">
              <motion.button
                type="button"
                whileTap={{ y: 1 }}
                onClick={onCancel}
                className="text-sm font-medium text-stone-400 hover:text-stone-200 px-3 py-2.5 rounded-full transition-colors"
              >
                {cancelText}
              </motion.button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.95, y: 1 }}
                onClick={onConfirm}
                className={`text-sm font-semibold px-5 py-2.5 rounded-full transition-colors ${
                  isDanger
                    ? "bg-red-500 text-white hover:bg-red-400"
                    : "bg-stone-100 text-black hover:bg-white"
                }`}
              >
                {confirmText}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
